import { Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import * as React from 'react';
import { _className, Button, ComponentSync, Grid, LL_H_C, ModuleFE_MouseInteractivity, mouseInteractivity_PopUp, openContent, TS_CollapsableContainer } from '@nu-art/thunderstorm-frontend/index';
import './Component_FindingResults.scss';
import { ModuleFE_Advisor_Value, ModuleFE_Advisor_Variable, ModuleFE_AdvisorSession, ModuleFE_EMInterface, ModuleFE_RuntimeFinding } from '@app/advisor-frontend';
import { advisorConfig_DevMode, ModuleFE_AdvisorSettings } from '../../../../../modules/ModuleFE_AdvisorSettings/index.js';
import { capitalizeFirstLetter, exists, formatTimestamp } from '@nu-art/ts-common';
import { isImplicitPositiveValue, resolveValueDisplay, valueID_No, valueID_Pos } from '@app/hcs-shared';
import { FindingType_Enumerated, FindingType_Numeric, FindingType_Textual } from '@app/advisor-shared';
import { ICONS } from '@app/styles';
import { getVarNormal, tooltipFindingsModules, tooltipFindingsTranslation } from '../../utils/tooltip-findings.js';
import { ColumnKey_MiddleColumn } from '@app/advisor-v5-shared';
import { Tooltip_Finding_Enum, Tooltip_Finding_Numeric, Tooltip_Finding_Textual } from '@app/advisor-ui-frontend/index';
import { ModuleFE_Spotlight } from '../../../../../modules/ModuleFE_Spotlight/index.js';
import { AdvisorConversion } from '../../../../../_utils/advisor-conversion.js';
import { ModuleFE_AdvisorAnalytics } from '../../../../../_analytics/consts.js';
import { ModuleFE_PanelFindings } from '../../../../../modules/ModuleFE_PanelFindings/ModuleFE_PanelFindings.js';
export class Component_FindingResults extends ComponentSync {
    textualFindingRef = React.createRef();
    //######################### Life Cycle #########################
    __onReadOnlyChanged() {
        this.forceUpdate();
    }
    __onAdvisorSettingsUpdated = (key) => {
        if (key !== 'hideNegative')
            return;
        this.forceUpdate();
    };
    __onAdvisorBoardTransitionEnd = (columnKey) => {
        if (columnKey !== ColumnKey_MiddleColumn || !this.textualFindingRef.current)
            return;
        // in many cases we encounter bad calculation of the collapsible container height
        // this will assure re calculation of the container height once the advisor column completed rendering
        // to its full height
        this.textualFindingRef.current.refreshContainerHeight();
    };
    deriveStateFromProps(nextProps, state) {
        state.findingResults = nextProps.findingResults;
        state.sectionConfig = nextProps.sectionConfig;
        state.overrideValue = nextProps.overrideValue;
        state.hideIfNormal = !!nextProps.hideIfNormal;
        state.forceTimestampOnTextual = !!nextProps.forceTimestampOnTextual;
        return state;
    }
    //######################### Logic #########################
    getFindingClassName = (finding) => {
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        return _className('finding', finding.type, this.state.sectionConfig.allowFindingEdit && 'edit-allowed', finding.encounterId !== currentEncounterId && 'fade');
    };
    onEditIconClick = async () => {
        if (!this.state.sectionConfig.allowFindingEdit || ModuleFE_EMInterface.isPostDisposition())
            return; // disable on disposition
        const finding = this.state.findingResults.findings[0];
        const parentFinding = finding.parentId ? ModuleFE_RuntimeFinding.cache.unique(finding.parentId) : finding;
        const findingToUpdate = AdvisorConversion.finding_findingToUpdate(parentFinding);
        if (findingToUpdate)
            //Open up finding to edit
            ModuleFE_Spotlight.openEditFindings({
                findings: {
                    processedRawFindings: [],
                    findingsToUpdate: [findingToUpdate],
                }
            });
    };
    // private calculateSpecialAttention = (variable: DB_Variable) => {
    // 	const answer = ModuleFE_RuntimeFinding.getSpecialAttention(variable);
    // 	if (!this.state.sectionConfig.specialAttention_Disease)
    // 		answer.disease = false;
    //
    // 	return answer;
    // };
    shouldRenderFinding = (finding) => {
        switch (finding.type) {
            case 'numeric':
                return true;
            case 'enumerated': {
                if (this.state.sectionConfig.forceShowNegative)
                    return true;
                const hideNegative = ModuleFE_AdvisorSettings.getAdvisorConfig('hideNegative');
                return !hideNegative || !ModuleFE_EMInterface.isNegativeValue(finding.valueIds[0]);
            }
        }
    };
    allowEdit = () => {
        //Textual findings are never editable
        if (this.state.findingResults.findings[0].type === FindingType_Textual)
            return false;
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        if (!this.state.sectionConfig.allowFindingEdit)
            return false;
        const readonly = ModuleFE_EMInterface.isReadonly();
        if (readonly)
            return false;
        return this.state.findingResults.findings[0].encounterId === currentEncounterId;
    };
    getContextMenu = (finding) => {
        const actions = [];
        if (finding.type === FindingType_Enumerated && ModuleFE_Advisor_Variable.isComplaint(finding.variableId)) {
            const isChiefComplaint = finding.isChiefComplaint;
            const label = isChiefComplaint ? 'Mark as an associated symptom' : 'Mark as chief complaint';
            actions.push({
                label: label,
                action: () => ModuleFE_RuntimeFinding.toggleChiefComplaint(finding),
            });
        }
        if (!actions.length)
            return {};
        return openContent.popUpV2.bottom({
            id: 'finding-context-menu',
            offset: 0,
            event: 'onContextMenu',
            content: () => {
                return actions.map((action, index) => _jsx(Button, { variant: 'text', onClick: async () => {
                        try {
                            await action.action();
                        }
                        catch (err) {
                            this.logError('Action failed', err);
                        }
                        finally {
                            ModuleFE_MouseInteractivity.hide(mouseInteractivity_PopUp);
                        }
                    }, children: action.label }, index));
            },
        });
    };
    isNormal = (finding) => {
        const variable = ModuleFE_Advisor_Variable.cache.unique(finding.variableId);
        if (!variable)
            return true;
        const normalRange = getVarNormal(variable);
        if (!normalRange)
            return true;
        return finding.value >= normalRange[0] && finding.value <= normalRange[1];
    };
    onFindingTooltipOpen = (finding) => {
        const currentEncounterId = ModuleFE_AdvisorSession.getCurrentEncounterId();
        const view = ModuleFE_PanelFindings.getCurrentOnScreenView();
        ModuleFE_AdvisorAnalytics.trigger.findings.findings_tooltipOpen({
            variableId: finding.variableId,
            currentEncounter: finding.encounterId === currentEncounterId,
            viewId: view._id,
            viewLabel: view.label,
        });
    };
    //######################### Render #########################
    render() {
        const latestFinding = this.state.findingResults.findings[0];
        switch (latestFinding.type) {
            case FindingType_Enumerated:
                return this.render_Enumerated(latestFinding);
            case FindingType_Numeric:
                return this.render_Numeric(latestFinding);
            case FindingType_Textual:
                return this.render_Textual(latestFinding);
            default: {
                this.logDebug(`No render method implemented for finding of type ${latestFinding.type}`);
                return _jsx(_Fragment, {});
            }
        }
    }
    render_SpecialAttentionIcons = (variable) => {
        // const specialAttention = this.calculateSpecialAttention(variable);
        return _jsx(_Fragment, {});
    };
    render_Name = (_var, contextMenu) => {
        const variable = typeof _var === 'string' ? ModuleFE_Advisor_Variable.cache.unique(_var) : _var;
        if (!variable)
            return _jsx("div", { className: 'finding__name', children: "-" });
        const name = variable.name.includes('.') ? variable.name.split('.')[1] : variable.name;
        return _jsxs(LL_H_C, { className: 'finding__name', ...contextMenu ?? {}, children: [name, this.render_SpecialAttentionIcons(variable)] });
    };
    render_Value = (_val, timestamp) => {
        //passed a number, assuming no inference is needed, just render
        let strings = new Set();
        let classes = new Set();
        classes.add('finding__value');
        if (this.state.sectionConfig.showDate && timestamp)
            strings.add(this.getTimestampString(timestamp));
        switch (typeof _val) {
            case 'number': {
                const varNormal = getVarNormal(this.state.findingResults.variable);
                const notInRange = exists(varNormal) && (_val < varNormal[0] || _val > varNormal[1]);
                if (notInRange)
                    classes.add('not-in-range');
                strings.add(String(_val));
                break;
            }
            case 'string': {
                const value = ModuleFE_Advisor_Value.cache.unique(_val);
                if (!value)
                    strings.add('-');
                else if (value?._id === valueID_No)
                    strings.add('No');
                else if (isImplicitPositiveValue(value?._id)) {
                    // Implicit positives (Yes / CareMap "Suspected") are suppressed
                    // unless the section explicitly opts in to writing them out.
                    if (this.state.sectionConfig.explicitYes)
                        strings.add(resolveValueDisplay(value, true));
                }
                else
                    strings.add(resolveValueDisplay(value));
                if (value?._id === valueID_Pos)
                    classes.add('not-in-range');
            }
        }
        if (!strings.size)
            return _jsx("div", { className: 'finding__value' });
        return _jsx("div", { className: _className(...Array.from(classes)), children: Array.from(strings).join(' - ') });
    };
    render_Attributes = (attributes) => {
        if (!this.state.sectionConfig.showAttributes || !attributes?.length)
            return;
        return _jsx(Grid, { className: 'finding__attributes', children: attributes.map(att => {
                const key = `${att.variableId}-${att.valueIds[0]}`;
                return _jsxs(React.Fragment, { children: [this.render_Name(att.variableId), this.render_Value(att.valueIds[0]), this.state.sectionConfig.allowFindingEdit &&
                            _jsx("div", { className: 'finding__attributes__edit-placeholder' })] }, key);
            }) });
    };
    render_EditIcon = () => {
        if (!this.allowEdit())
            return;
        return _jsx(ICONS.advisor_v5_freeText.component, { className: 'finding__edit-icon', onClick: this.onEditIconClick });
    };
    render_Timestamp = (finding) => {
        const timestamp = finding.issuedTimestamp ?? finding.__created;
        return _jsx("div", { className: 'finding__timestamp', children: this.getTimestampString(timestamp) });
    };
    isSameDay = (timestamp) => {
        const date = new Date(timestamp);
        const today = new Date();
        return today.getFullYear() === date.getFullYear()
            && today.getMonth() === date.getMonth()
            && today.getDate() === date.getDate();
    };
    getTimestampString = (timestamp) => {
        if (this.isSameDay(timestamp))
            return formatTimestamp('hh:mmA', timestamp);
        return formatTimestamp('MM.DD.YYYY • hh:mmA', timestamp);
    };
    //######################### Render - Enumerated #########################
    render_Enumerated = (finding) => {
        const shouldRender = this.shouldRenderFinding(finding);
        if (!shouldRender)
            return;
        const className = this.getFindingClassName(finding);
        const contextMenu = this.getContextMenu(finding);
        if (this.state.sectionConfig.showFindingInfoTooltip) {
            const tooltipResults = tooltipFindingsTranslation(this.state.findingResults);
            return _jsxs(Grid, { className: className, children: [_jsx(Tooltip_Finding_Enum, { data: tooltipResults, modules: tooltipFindingsModules, showSource: ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_DevMode), onTooltipOpened: () => this.onFindingTooltipOpen(finding), children: this.render_Name(finding.variableId, contextMenu) }), this.render_Value(finding.valueIds[0], finding.issuedTimestamp), this.render_EditIcon(), this.render_Attributes(finding.attributes)] });
        }
        return _jsxs(Grid, { className: className, children: [this.render_Name(finding.variableId, contextMenu), this.render_Value(finding.valueIds[0], finding.issuedTimestamp), this.render_EditIcon(), this.render_Attributes(finding.attributes)] });
    };
    render_Numeric = (finding) => {
        const className = this.getFindingClassName(finding);
        const isNormal = this.isNormal(finding);
        if (isNormal && this.state.hideIfNormal)
            return;
        if (this.state.sectionConfig.showFindingInfoTooltip) {
            const tooltipResults = tooltipFindingsTranslation(this.state.findingResults);
            return _jsxs(Grid, { className: className, children: [_jsx(Tooltip_Finding_Numeric, { data: tooltipResults, modules: tooltipFindingsModules, showSource: ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_DevMode), onTooltipOpened: () => this.onFindingTooltipOpen(finding), children: this.render_Name(finding.variableId) }), this.render_Value(finding.value, finding.issuedTimestamp), this.render_EditIcon()] });
        }
        return _jsxs(Grid, { className: className, children: [this.render_Name(finding.variableId), this.render_Value(finding.value, finding.issuedTimestamp), this.render_EditIcon()] });
    };
    render_Textual = (finding) => {
        const className = this.getFindingClassName(finding);
        const otherTextuals = this.state.findingResults.findings.filter(f => f._id !== finding._id
            && f.type === FindingType_Textual
            && f.issuedTimestamp === finding.issuedTimestamp);
        const text = [finding.value ?? '', ...otherTextuals.map(f => f.value ?? '')]
            .map(str => str.length ? capitalizeFirstLetter(str) : '')
            .join('\n\n');
        const HeaderRenderer = () => {
            const content = () => _jsxs(LL_H_C, { className: 'finding__title', children: [this.render_Name(finding.variableId), this.state.forceTimestampOnTextual && this.render_Timestamp(finding)] });
            if (this.state.sectionConfig.showFindingInfoTooltip) {
                const tooltipResults = tooltipFindingsTranslation(this.state.findingResults);
                return _jsx(Tooltip_Finding_Textual, { data: tooltipResults, modules: tooltipFindingsModules, showSource: ModuleFE_AdvisorSettings.getAdvisorConfig(advisorConfig_DevMode), onTooltipOpened: () => this.onFindingTooltipOpen(finding), children: content() });
            }
            return content();
        };
        return _jsx(TS_CollapsableContainer, { className: className, initialCollapsed: false, ref: this.textualFindingRef, flipHeaderOrder: true, customCaret: () => _jsx(ICONS.advisor_v4_arrow.component, {}), headerRenderer: HeaderRenderer, containerRenderer: () => {
                return _jsxs(_Fragment, { children: [this.state.overrideValue && _jsx("div", { className: 'finding__value', children: this.state.overrideValue }), _jsx("div", { className: 'finding__value', children: text })] });
            } });
    };
}
//# sourceMappingURL=Component_FindingResults.js.map